import React, { useState, useEffect, useReducer, useContext } from "react";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@material-ui/core";

import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import FlowBuilderModal from "../../components/FlowBuilderModal";
import NewTicketModal from "../../components/NewTicketModal";
import { AdminNav, normalizeSearch } from "../../components/AdminWorkspace";
import { usePanelStyles } from "../../components/QueueIntegrationModal/panelKit";

import { i18n } from "../../translate/i18n";
import MainContainer from "../../components/MainContainer";
import { PageHeader, StatCard, StatusBadge, EmptyState } from "../../components/ui";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getTokens } from "../../styles/tokens";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => {
  const tk = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    stats: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 14,
      marginBottom: 16,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)", gap: 10 },
    },
    statusFilter: { width: 170, [theme.breakpoints.down("xs")]: { flex: "1 1 140px", width: "auto" } },
    row: { cursor: "pointer" },
    flowCell: { display: "flex", alignItems: "center", gap: 12, minWidth: 0 },
    flowIcon: {
      flex: "none",
      width: 34,
      height: 34,
      borderRadius: tk.radius.sm,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: tk.brand,
      backgroundColor: tk.tint.soft,
      "& .MuiSvgIcon-root": { fontSize: 19 },
    },
    flowText: { minWidth: 0 },
    flowName: { overflowWrap: "anywhere" },
    hideXs: { [theme.breakpoints.down("xs")]: { display: "none" } },
    menuIcon: { minWidth: 32, color: "inherit", "& .MuiSvgIcon-root": { fontSize: 19 } },
  };
});

const FlowBuilder = () => {
  const classes = useStyles();
  const panel = usePanelStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [webhooks, setWebhooks] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWebhookName, setSelectedWebhookName] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDuplicateOpen, setConfirmDuplicateOpen] = useState(false);

  const [hasMore, setHasMore] = useState(false);
  const [reloadData, setReloadData] = useState(false);
  const { user, socket } = useContext(AuthContext);
  // menu "⋮" da linha: uma instância só, ancorada na linha clicada
  const [rowMenu, setRowMenu] = useState({ anchorEl: null, flow: null });

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/flowbuilder");
          setWebhooks(data.flows);
          dispatch({ type: "LOAD_CONTACTS", payload: data.flows });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [reloadData]);

  useEffect(() => {
    const companyId = user.companyId;

    const onContact = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    };

    if (!socket) return;
    socket.on(`company-${companyId}-contact`, onContact);

    return () => {
      socket.off(`company-${companyId}-contact`, onContact);
    };
  }, [socket, user.companyId]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleEditContact = (contact) => {
    setSelectedContactId(contact.id);
    setSelectedWebhookName(contact.name);
    setContactModalOpen(true);
  };

  const handleDeleteWebhook = async (webhookId) => {
    try {
      await api.delete(`/flowbuilder/${webhookId}`);
      setDeletingContact(null);
      setConfirmOpen(false);
      setReloadData((old) => !old);
      toast.success("Fluxo excluído com sucesso");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDuplicateFlow = async (flowId) => {
    try {
      await api.post(`/flowbuilder/duplicate`, { flowId: flowId });
      setDeletingContact(null);
      setConfirmDuplicateOpen(false);
      setReloadData((old) => !old);
      toast.success("Fluxo duplicado com sucesso");
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const openEditor = (flowId) => history.push(`/flowbuilder/${flowId}`);

  const closeRowMenu = () => setRowMenu((old) => ({ ...old, anchorEl: null }));

  // fecha o menu antes de agir, para o foco não disputar com o modal que abre
  const runRowAction = (action) => {
    const flow = rowMenu.flow;
    closeRowMenu();
    if (flow) setTimeout(() => action(flow), 100);
  };

  const hasFilter = Boolean(searchParam) || statusFilter !== "all";

  const filteredWebhooks = webhooks.filter(flow => normalizeSearch(flow.name).includes(normalizeSearch(searchParam)) && (statusFilter === "all" || Boolean(flow.active) === (statusFilter === "active")));


  return (
    <MainContainer maxWidth={1440} scroll>
      {/* Modais - mantendo todos originais */}
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />

      <FlowBuilderModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        flowId={selectedContactId}
        nameWebhook={selectedWebhookName}
        onSave={() => setReloadData((old) => !old)}
      />

      <ConfirmationModal
        title={
          deletingContact
            ? `Excluir fluxo "${deletingContact.name}"?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => {
          if (deletingContact) {
            handleDeleteWebhook(deletingContact.id);
          }
        }}
      >
        {deletingContact
          ? `Esta ação não pode ser desfeita. Todas as integrações relacionadas serão perdidas.`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>

      <ConfirmationModal
        title={
          deletingContact
            ? `Duplicar fluxo "${deletingContact.name}"?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmDuplicateOpen}
        onClose={setConfirmDuplicateOpen}
        onConfirm={() => {
          if (deletingContact) {
            handleDuplicateFlow(deletingContact.id);
          }
        }}
      >
        {deletingContact
          ? `Uma cópia do fluxo será criada para você editar.`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>

      <PageHeader
        title="Fluxos de conversa"
        subtitle="Organize as jornadas do chatbot e abra o editor para conectar mensagens, menus e integrações."
        badge={<StatusBadge color="neutral" dot={false} label={`${webhooks.length}`} />}
        actions={
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenContactModal}>
            Novo fluxo
          </Button>
        }
      />
      <AdminNav />

      <div className={classes.stats}>
        <StatCard label="Fluxos cadastrados" value={webhooks.length} icon={<AccountTreeOutlinedIcon />} tone="brand" />
        <StatCard label="Ativos" value={webhooks.filter((flow) => flow.active).length} tone="success" />
        <StatCard label="Inativos" value={webhooks.filter((flow) => !flow.active).length} tone="neutral" />
      </div>

      <div className={panel.toolbar}>
        <TextField
          className={panel.toolbarGrow}
          type="search"
          size="small"
          variant="outlined"
          value={searchParam}
          onChange={(event) => setSearchParam(event.target.value)}
          placeholder="Buscar fluxo pelo nome…"
          inputProps={{ "aria-label": "Buscar fluxo pelo nome" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          className={classes.statusFilter}
          size="small"
          variant="outlined"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          inputProps={{ "aria-label": "Filtrar por status" }}
        >
          <MenuItem value="all">Todos os status</MenuItem>
          <MenuItem value="active">Ativos</MenuItem>
          <MenuItem value="inactive">Inativos</MenuItem>
        </TextField>
      </div>

      <Paper
        className={`${panel.tableCard} ${panel.tableCardAuto}`}
        elevation={0}
        onScroll={handleScroll}
      >
        {!loading && filteredWebhooks.length === 0 ? (
          <EmptyState
            icon={<AccountTreeOutlinedIcon />}
            title={hasFilter ? "Nenhum fluxo encontrado" : "Nenhum fluxo criado ainda"}
            message={
              hasFilter
                ? "Tente outro nome ou troque o filtro de status."
                : "Crie seu primeiro fluxo de conversa para automatizar atendimentos."
            }
            action={
              !hasFilter && (
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenContactModal}>
                  Criar primeiro fluxo
                </Button>
              )
            }
          />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fluxo</TableCell>
                <TableCell>Status</TableCell>
                <TableCell className={classes.hideXs}>Atualizado em</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredWebhooks.map((flow) => (
                <TableRow
                  key={flow.id}
                  hover
                  className={classes.row}
                  tabIndex={0}
                  aria-label={`Abrir fluxo ${flow.name}`}
                  onClick={() => openEditor(flow.id)}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openEditor(flow.id);
                    }
                  }}
                >
                  <TableCell>
                    <div className={classes.flowCell}>
                      <span className={classes.flowIcon} aria-hidden="true">
                        <AccountTreeOutlinedIcon />
                      </span>
                      <div className={classes.flowText}>
                        <div className={`${panel.cellStrong} ${classes.flowName}`}>{flow.name}</div>
                        <div className={panel.cellMuted}>Fluxo #{flow.id}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={flow.active ? "active" : "inactive"} />
                  </TableCell>
                  <TableCell className={`${panel.cellMuted} ${classes.hideXs}`}>
                    {flow.updatedAt ? new Date(flow.updatedAt).toLocaleDateString("pt-BR") : "—"}
                  </TableCell>
                  <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                    <div className={panel.rowActions}>
                      <Tooltip title="Abrir editor">
                        <IconButton aria-label={`Abrir editor do fluxo ${flow.name}`} onClick={() => openEditor(flow.id)}>
                          <OpenInNewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar nome">
                        <IconButton
                          className={classes.hideXs}
                          aria-label={`Editar nome do fluxo ${flow.name}`}
                          onClick={() => handleEditContact(flow)}
                        >
                          <EditOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mais ações">
                        <IconButton
                          aria-label={`Mais ações do fluxo ${flow.name}`}
                          aria-haspopup="menu"
                          onClick={(event) => setRowMenu({ anchorEl: event.currentTarget, flow })}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={4} />}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Menu
        anchorEl={rowMenu.anchorEl}
        open={Boolean(rowMenu.anchorEl)}
        onClose={closeRowMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => runRowAction((flow) => openEditor(flow.id))}>
          <ListItemIcon className={classes.menuIcon}>
            <OpenInNewIcon />
          </ListItemIcon>
          Abrir editor
        </MenuItem>
        <MenuItem onClick={() => runRowAction((flow) => handleEditContact(flow))}>
          <ListItemIcon className={classes.menuIcon}>
            <EditOutlinedIcon />
          </ListItemIcon>
          Editar nome
        </MenuItem>
        <MenuItem
          onClick={() =>
            runRowAction((flow) => {
              setDeletingContact(flow);
              setConfirmDuplicateOpen(true);
            })
          }
        >
          <ListItemIcon className={classes.menuIcon}>
            <FileCopyOutlinedIcon />
          </ListItemIcon>
          Duplicar fluxo
        </MenuItem>
        <MenuItem
          className={panel.danger}
          onClick={() =>
            runRowAction((flow) => {
              setDeletingContact(flow);
              setConfirmOpen(true);
            })
          }
        >
          <ListItemIcon className={classes.menuIcon}>
            <DeleteOutlineIcon />
          </ListItemIcon>
          Excluir fluxo
        </MenuItem>
      </Menu>
    </MainContainer>
  );
};

export default FlowBuilder;
